import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_TEXT_COMMAND,
  PASTE_COMMAND,
} from 'lexical';
import {useEffect} from 'react';

/**
 * PlainTextPlugin prevents all rich text formatting while keeping the editor functional.
 * - Blocks keyboard shortcuts for bold, italic, underline, strikethrough, etc.
 * - Strips all formatting from pasted content, keeping only plain text
 *
 * Note: This still allows comments (HighlightNodes) and other non-formatting features.
 */
export const PlainTextPlugin = (): null => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Block all FORMAT_TEXT_COMMAND formatting (bold, italic, underline, etc.)
    const removeFormatListener = editor.registerCommand(
      FORMAT_TEXT_COMMAND,
      () => true, // Return true to indicate we've handled (blocked) the command
      COMMAND_PRIORITY_CRITICAL
    );

    // Intercept paste and strip all formatting
    const removePasteListener = editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const clipboardEvent = event as ClipboardEvent;
        const clipboardData = clipboardEvent.clipboardData;

        if (!clipboardData) {
          return false;
        }

        // Get plain text from clipboard, stripping all formatting
        const plainText = clipboardData.getData('text/plain');

        if (!plainText) {
          return false;
        }

        // Prevent the default paste behavior
        clipboardEvent.preventDefault();

        // Insert the plain text at current selection
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            // If there's selected text, delete it first
            if (!selection.isCollapsed()) {
              selection.removeText();
            }

            // Split text by newlines and insert as separate paragraphs
            const lines = plainText.split(/\r?\n/);

            if (lines.length === 1) {
              // Single line - just insert the text
              selection.insertText(lines[0]);
            } else {
              // Multiple lines - insert first line, then create new paragraphs for rest
              selection.insertText(lines[0]);

              for (let i = 1; i < lines.length; i++) {
                // Insert a paragraph break
                selection.insertParagraph();
                // Insert the text for this line
                if (lines[i]) {
                  selection.insertText(lines[i]);
                }
              }
            }
          }
        });

        return true; // We've handled the paste
      },
      COMMAND_PRIORITY_CRITICAL
    );

    return () => {
      removeFormatListener();
      removePasteListener();
    };
  }, [editor]);

  return null;
};
